import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    Chip,
} from '@mui/material';
import {
    Check as CheckIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Search as SearchIcon,
    FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import timesheetService from '../../../services/timesheetService';
import useDictation from '../../../hooks/useDictation';

export default function DailyNotesModal({
    open,
    onClose,
    jobs,
    allJobs,
    records,
    dailyNotes,
    currentDate,
    onSave,
}) {
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [noteTexts, setNoteTexts] = useState({});
    const [savedJobs, setSavedJobs] = useState(new Set());
    const [search, setSearch] = useState('');
    const debounceTimers = useRef({});

    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000,
    });
    const dictationJobRef = useRef(null);

    const toggleDictation = useCallback(async () => {
        if (!dictationSupported || !selectedJobId) return;
        if (isListening && dictationJobRef.current === selectedJobId) {
            stopDictation();
            dictationJobRef.current = null;
            return;
        }
        dictationJobRef.current = selectedJobId;
        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !dictationJobRef.current) return;
                const jobId = dictationJobRef.current;
                setNoteTexts(prev => {
                    const next = ((prev[jobId] || '') + (prev[jobId] ? ' ' : '') + transcript).trim();
                    if (debounceTimers.current[jobId]) clearTimeout(debounceTimers.current[jobId]);
                    debounceTimers.current[jobId] = setTimeout(() => {
                        onSave(jobId, next);
                        setSavedJobs(s => new Set(s).add(jobId));
                    }, 500);
                    return { ...prev, [jobId]: next };
                });
            },
            onEnd: () => { dictationJobRef.current = null; },
        });
    }, [dictationSupported, selectedJobId, isListening, startDictation, stopDictation, onSave]);

    // Index jobs once — compute hours and note flags per job.
    const indexedJobs = useMemo(() => {
        const hoursByJob = new Map();
        for (const r of records || []) {
            if (!r.isDeleted && r.hours > 0) {
                hoursByJob.set(r.jobId, (hoursByJob.get(r.jobId) || 0) + r.hours);
            }
        }
        return jobs.map(j => {
            const name = timesheetService.getJobName(j) || '';
            const code = timesheetService.getJobCode(j) || '';
            return {
                ...j,
                _name: name,
                _code: code,
                _nameLc: `${name} ${code}`.toLowerCase(),
                _hours: hoursByJob.get(j.id) || 0,
            };
        });
    }, [jobs, records]);

    const sortedJobs = useMemo(() => {
        const term = search.trim().toLowerCase();
        const matched = term
            ? indexedJobs.filter(j => j._nameLc.includes(term))
            : indexedJobs;
        // With-hours first, then the rest — but keep each group alphabetical.
        const withHours = matched.filter(j => j._hours > 0).sort((a, b) => a._name.localeCompare(b._name));
        const withoutHours = matched.filter(j => !j._hours).sort((a, b) => a._name.localeCompare(b._name));
        return { withHours, withoutHours };
    }, [indexedJobs, search]);

    const initDoneRef = useRef(false);
    useEffect(() => {
        if (!open) {
            initDoneRef.current = false;
            return;
        }
        if (initDoneRef.current) return;

        const texts = {};
        for (const job of indexedJobs) {
            const existing = dailyNotes.find(n =>
                n.jobId === job.id && n.noteDate === currentDate && !n.employeeId
            );
            texts[job.id] = existing?.notes || '';
        }
        setNoteTexts(texts);
        setSavedJobs(new Set());
        // Preselect first job with hours, else first job
        const firstWithHours = indexedJobs.find(j => j._hours > 0);
        setSelectedJobId((firstWithHours || indexedJobs[0])?.id || null);
        setSearch('');
        initDoneRef.current = true;
    }, [open, indexedJobs, dailyNotes, currentDate]);

    const handleNoteChange = useCallback((jobId, text) => {
        setNoteTexts(prev => ({ ...prev, [jobId]: text }));
        if (debounceTimers.current[jobId]) clearTimeout(debounceTimers.current[jobId]);
        debounceTimers.current[jobId] = setTimeout(() => {
            onSave(jobId, text);
            setSavedJobs(prev => new Set(prev).add(jobId));
        }, 500);
    }, [onSave]);

    const handleClose = useCallback(() => {
        Object.values(debounceTimers.current).forEach(clearTimeout);
        if (selectedJobId && noteTexts[selectedJobId] !== undefined) {
            onSave(selectedJobId, noteTexts[selectedJobId]);
        }
        onClose();
    }, [selectedJobId, noteTexts, onSave, onClose]);

    useEffect(() => {
        return () => { Object.values(debounceTimers.current).forEach(clearTimeout); };
    }, []);

    if (jobs.length === 0) return null;

    const notesCount = Object.values(noteTexts).filter(t => t && t.trim()).length;
    const selectedJob = indexedJobs.find(j => j.id === selectedJobId);


    const renderJobRow = (job) => {
        const hasNote = noteTexts[job.id] && noteTexts[job.id].trim();
        const isSaved = savedJobs.has(job.id);
        const isSelected = selectedJobId === job.id;
        return (
            <button
                key={job.id}
                type="button"
                onClick={() => setSelectedJobId(job.id)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: isSelected ? '#e3f2fd' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                }}
            >
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: 13,
                        fontWeight: isSelected ? 600 : 500,
                        color: '#222',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {job._code && (
                            <span style={{ color: '#1976d2', fontWeight: 700, marginRight: 4 }}>
                                #{job._code}
                            </span>
                        )}
                        {job._name}
                    </div>
                    {job._hours > 0 && (
                        <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 500 }}>
                            {job._hours}h
                        </div>
                    )}
                </div>
                {hasNote && <DotIcon sx={{ fontSize: 10, color: 'warning.main' }} />}
                {isSaved && <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />}
            </button>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                Daily Job Notes — {currentDate
                    ? new Date(currentDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })
                    : ''}
                <Typography variant="caption" display="block" color="text.secondary">
                    {notesCount}/{jobs.length} jobs with notes
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
                {/* Left column — jobs list */}
                <Box sx={{
                    width: { xs: '40%', sm: 260 },
                    minWidth: 180,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search job..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto', py: 0.5 }}>
                        {sortedJobs.withHours.length > 0 && (
                            <>
                                <Typography variant="caption" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'success.dark', fontWeight: 600 }}>
                                    With hours ({sortedJobs.withHours.length})
                                </Typography>
                                {sortedJobs.withHours.map(renderJobRow)}
                            </>
                        )}
                        {sortedJobs.withoutHours.length > 0 && (
                            <>
                                <Divider sx={{ my: 0.5 }}>
                                    <Typography variant="caption" color="text.disabled">
                                        No hours ({sortedJobs.withoutHours.length})
                                    </Typography>
                                </Divider>
                                {sortedJobs.withoutHours.map(renderJobRow)}
                            </>
                        )}
                        {sortedJobs.withHours.length === 0 && sortedJobs.withoutHours.length === 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 2, display: 'block', textAlign: 'center' }}>
                                No matches
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Right column — note editor */}
                <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {selectedJob ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                                    {selectedJob._code && (
                                        <span style={{ color: '#1976d2', fontWeight: 700, marginRight: 6 }}>
                                            #{selectedJob._code}
                                        </span>
                                    )}
                                    {selectedJob._name}
                                </Typography>
                                {selectedJob._hours > 0 && (
                                    <Chip label={`${selectedJob._hours}h`} size="small" color="success" variant="outlined" />
                                )}
                                {savedJobs.has(selectedJobId) && (
                                    <Chip icon={<CheckIcon />} label="Saved" size="small" color="success" sx={{ height: 22 }} />
                                )}
                            </Box>

                            <TextField
                                multiline
                                fullWidth
                                placeholder={dictationSupported ? "Type or dictate notes for this job..." : "Enter notes for this job..."}
                                value={noteTexts[selectedJobId] || ''}
                                onChange={(e) => handleNoteChange(selectedJobId, e.target.value)}
                                autoFocus
                                InputProps={{
                                    endAdornment: dictationSupported && (
                                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <IconButton
                                                onClick={toggleDictation}
                                                color={isListening && dictationJobRef.current === selectedJobId ? 'error' : 'default'}
                                                size="small"
                                                title={isListening ? 'Stop recording' : 'Start voice input'}
                                            >
                                                {isListening && dictationJobRef.current === selectedJobId ? <MicOffIcon /> : <MicIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    flex: 1,
                                    '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                                    '& textarea': { height: '100% !important', overflow: 'auto !important' },
                                }}
                            />
                        </>
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            Select a job to add notes
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">Close</Button>
            </DialogActions>
        </Dialog>
    );
}
